"use client";

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import type { DialogNode, TopicVocabulary } from "@/lib/dialog-data";
import { EMPTY_VOCABULARY } from "@/lib/constants";
import { MessageCircle, User, CheckCircle2, Sparkles, HelpCircle, Plus, RotateCcw, ThumbsUp, ThumbsDown, Pencil, BookOpen, Loader2 } from "lucide-react";
import { SpeakerButton } from "./speaker-button";
import { cn } from "@/lib/utils";
import { WordLookup, TappableText, buildDictionary } from "./word-lookup";
import { useTTS } from "@/lib/use-tts";
import { PhraseChatModal } from "./phrase-chat-modal";
import { CustomPhraseModal } from "./dialog/custom-phrase-modal";
import { FixResponseModal } from "./dialog/fix-response-modal";
import { NodePracticeModal } from "./dialog/node-practice-modal";
import { useLanguage } from "@/lib/language-context";
import { useSettings } from "@/lib/settings-context";
import { useTranslations } from "@/lib/use-translations";
import { useVocabPrefetch } from "@/lib/hooks/use-vocab-prefetch";
import api from "@/lib/api/client";
import { useNodeRewrite } from "@/hooks/use-node-rewrite";
import { DialogueFeedbackCard } from "./dialogue-feedback";

interface DialogTreeProps {
  nodes: DialogNode[];
  vocabulary?: TopicVocabulary;
  speaker?: string;
  topicTitle?: string;
  /** Opik trace ID for feedback correlation */
  traceId?: string;
  onNodesChange?: (nodes: DialogNode[]) => void;
  onModalChange?: (isOpen: boolean) => void;
  isStreaming?: boolean;
}

// Individual tree node component
function TreeNode({
  node,
  isActive,
  showAvatar,
  speaker,
  ttsState,
  feedback,
  isPendingChoice,
  showVocalization,
  onToggle,
  onSpeak,
  onStop,
  onAskHelp,
  onFix,
  onFeedback,
  onLongPress,
  isStartNode,
  isRewriting,
  t,
}: {
  node: DialogNode;
  isActive: boolean;
  showAvatar: boolean;
  speaker?: string;
  ttsState: "idle" | "loading" | "playing";
  feedback?: "up" | "down" | null;
  isPendingChoice?: boolean;
  showVocalization?: boolean;
  onToggle: () => void;
  onSpeak: (text: string) => void;
  onStop: () => void;
  onAskHelp: () => void;
  onFix: () => void;
  onFeedback: (rating: "up" | "down") => void;
  onLongPress: () => void;
  isStartNode?: boolean;
  isRewriting?: boolean;
  t: ReturnType<typeof useTranslations>;
}) {
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const didLongPress = useRef(false);

  const handleTouchStart = useCallback(() => {
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      onLongPress();
    }, 500);
  }, [onLongPress]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchMove = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleClick = useCallback(() => {
    // Don't toggle if we just did a long press
    if (didLongPress.current) {
      didLongPress.current = false;
      return;
    }
    onToggle();
  }, [onToggle]);
  const isUserMessage = node.type === "option";
  const isEnd = node.type === "end";
  const isStart = node.type === "start";

  return (
    <div
      className={cn(
        "group relative flex gap-2",
        isUserMessage ? "flex-row-reverse" : "flex-row"
      )}
    >
      {/* Avatar - only show if different from previous speaker */}
      <div className={cn(
        "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1",
        isUserMessage ? "bg-primary" : "bg-muted",
        !showAvatar && "invisible"
      )}>
        {isUserMessage ? (
          <User className="w-4 h-4 text-primary-foreground" />
        ) : (
          <MessageCircle className="w-4 h-4 text-muted-foreground" />
        )}
      </div>

      {/* Message bubble */}
      <div
        className={cn(
          "relative max-w-[80%] cursor-pointer transition-all duration-200",
          "active:scale-[0.98]",
          isActive && "z-10"
        )}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        {/* Bubble content */}
        <div
          {...(isStart && isStartNode ? { "data-coach-mark": "start-node" } : {})}
          className={cn(
            "rounded-2xl px-4 py-3 shadow-sm border",
            // User messages (options) - right aligned, different color
            isUserMessage && [
              "bg-primary text-primary-foreground border-primary",
              "rounded-br-md",
            ],
            // NPC/response messages - left aligned, subtle warm tint for presence
            !isUserMessage && [
              "bg-gradient-to-br from-card to-card/90 border-border/80",
              "rounded-bl-md shadow-[0_1px_3px_rgba(0,0,0,0.04)]",
            ],
            // Start node special styling
            isStart && "bg-gradient-to-br from-primary/10 to-primary/5 border-primary/30",
            // End node special styling
            isEnd && "bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/30",
            // Active state
            isActive && !isUserMessage && "ring-2 ring-primary/20",
            isActive && isUserMessage && "ring-2 ring-primary-foreground/20",
            // Pending choice - pulsing glow and dashed border to invite interaction
            isPendingChoice && "animate-pulse-glow border-dashed border-[3px]",
            isPendingChoice && isUserMessage && "border-white/60",
            isPendingChoice && !isUserMessage && "border-muted-foreground/40",
            // Rewriting state - subtle pulse to indicate background update
            isRewriting && "animate-pulse opacity-70"
          )}
        >
          {/* Type indicator */}
          <div className="flex items-center gap-2 mb-1.5">
            <div className={cn(
              "flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide",
              isUserMessage ? "text-primary-foreground/80" : "text-muted-foreground"
            )}>
              {isEnd && <CheckCircle2 className="w-3 h-3 text-green-500" />}
              <span>
                {isStart ? t.learning.conversationStarts :
                 isUserMessage ? t.learning.you :
                 isEnd ? t.learning.end :
                 speaker || t.learning.them}
              </span>
            </div>
          </div>

          {/* Main text */}
          <p className={cn(
            "text-base font-medium leading-relaxed",
            isUserMessage ? "text-primary-foreground" : "text-foreground"
          )}>
            <TappableText text={node.text} />
          </p>

          {/* Translation */}
          <p className={cn(
            "text-sm mt-1.5 italic leading-relaxed",
            isUserMessage ? "text-primary-foreground/85" : "text-foreground/60"
          )}>
            {node.translation}
          </p>

          {/* Vocalization tip - how to pronounce */}
          {showVocalization && node.vocalizationTip && (
            <p className={cn(
              "text-sm mt-1 font-mono",
              isUserMessage ? "text-primary-foreground/80" : "text-primary/80"
            )}>
              🗣️ {node.vocalizationTip}
            </p>
          )}

          {/* Alternatives (shown when active) */}
          {node.alternatives && node.alternatives.length > 0 && isActive && (
            <div className={cn(
              "mt-3 pt-3 border-t",
              isUserMessage ? "border-primary-foreground/20" : "border-border"
            )}>
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles className={cn(
                  "w-3 h-3",
                  isUserMessage ? "text-primary-foreground/80" : "text-muted-foreground"
                )} />
                <span className={cn(
                  "text-[11px] font-medium uppercase tracking-wide",
                  isUserMessage ? "text-primary-foreground/80" : "text-muted-foreground"
                )}>
                  {t.learning.alsoSay}
                </span>
              </div>
              <div className="space-y-2">
                {node.alternatives.map((alt, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={cn(
                      "block w-full text-left p-2 rounded-lg transition-colors",
                      isUserMessage
                        ? "bg-primary-foreground/10 hover:bg-primary-foreground/20"
                        : "bg-muted/50 hover:bg-muted"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSpeak(alt.text);
                    }}
                  >
                    <p className={cn(
                      "text-sm",
                      isUserMessage ? "text-primary-foreground" : "text-foreground"
                    )}>
                      {alt.text}
                    </p>
                    <p className={cn(
                      "text-xs italic mt-0.5",
                      isUserMessage ? "text-primary-foreground/80" : "text-muted-foreground"
                    )}>
                      {alt.translation}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Footer with actions */}
          <div className={cn(
            "flex items-center justify-between mt-2 pt-2 border-t",
            isUserMessage ? "border-primary-foreground/20" : "border-border/50"
          )}>
            <div className="flex items-center gap-1" {...(isStartNode ? { "data-coach-mark": "speaker-button" } : {})}>
              {/* Listen button */}
              <SpeakerButton
                state={ttsState}
                onClick={(e) => {
                  e.stopPropagation();
                  if (ttsState === "playing") {
                    onStop();
                  } else {
                    onSpeak(node.text);
                  }
                }}
                className={cn(
                  "p-1.5 rounded transition-colors",
                  ttsState !== "idle"
                    ? isUserMessage
                      ? "text-primary-foreground bg-primary-foreground/20"
                      : "text-primary bg-primary/10"
                    : isUserMessage
                      ? "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
                iconClassName={isUserMessage ? "text-primary-foreground" : ""}
              />

              {/* Ask help button */}
              <button
                type="button"
                title={t.learning.askAI}
                className={cn(
                  "p-1.5 rounded transition-colors",
                  isUserMessage
                    ? "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onAskHelp();
                }}
              >
                <HelpCircle className="w-4 h-4" />
              </button>

              {/* Fix button - only for non-user messages */}
              {!isUserMessage && (
                <button
                  type="button"
                  title={t.learning.fixThisResponse || "Fix"}
                  className="p-1.5 rounded transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFix();
                  }}
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}

              {/* Practice/vocabulary button */}
              <button
                type="button"
                title={t.dialog?.practice || "Practice"}
                className={cn(
                  "p-1.5 rounded transition-colors",
                  isUserMessage
                    ? "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onLongPress();
                }}
              >
                <BookOpen className="w-4 h-4" />
              </button>
            </div>

            {/* Feedback buttons */}
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                className={cn(
                  "p-1.5 rounded transition-colors",
                  feedback === "up"
                    ? isUserMessage
                      ? "text-primary-foreground bg-primary-foreground/20"
                      : "text-green-500 bg-green-500/10"
                    : isUserMessage
                      ? "text-primary-foreground/65 hover:text-primary-foreground/90"
                      : "text-muted-foreground/60 hover:text-green-500"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onFeedback("up");
                }}
              >
                <ThumbsUp className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                className={cn(
                  "p-1.5 rounded transition-colors",
                  feedback === "down"
                    ? isUserMessage
                      ? "text-primary-foreground bg-primary-foreground/20"
                      : "text-red-500 bg-red-500/10"
                    : isUserMessage
                      ? "text-primary-foreground/65 hover:text-primary-foreground/90"
                      : "text-muted-foreground/60 hover:text-red-500"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onFeedback("down");
                }}
              >
                <ThumbsDown className="w-3.5 h-3.5" />
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export function DialogTree({ nodes: initialNodes, vocabulary = EMPTY_VOCABULARY, speaker, topicTitle, traceId, onNodesChange, onModalChange, isStreaming }: DialogTreeProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [activeNode, setActiveNode] = useState<string | null>("start");
  // Track which child was selected for each parent node
  const [selectedChild, setSelectedChild] = useState<Map<string, string>>(new Map());
  // Track which node's chat modal is open
  const [chatModalNode, setChatModalNode] = useState<DialogNode | null>(null);
  // Track which node is currently being spoken
  const [speakingNodeId, setSpeakingNodeId] = useState<string | null>(null);
  // Dynamic nodes (injected by user)
  const [dynamicNodes, setDynamicNodes] = useState<DialogNode[]>([]);
  // Custom phrase modal state
  const [customPhraseParent, setCustomPhraseParent] = useState<DialogNode | null>(null);
  // Feedback state: nodeId -> "up" | "down"
  const [feedbackMap, setFeedbackMap] = useState<Map<string, "up" | "down">>(new Map());
  // Fix response modal state
  const [fixResponseNode, setFixResponseNode] = useState<DialogNode | null>(null);
  // Practice modal state (long press on node)
  const [practiceNode, setPracticeNode] = useState<DialogNode | null>(null);
  // Track node to scroll to after expansion
  const [scrollToNodeId, setScrollToNodeId] = useState<string | null>(null);
  // Track nodes being rewritten after a fix
  const [rewritingNodeIds, setRewritingNodeIds] = useState<Set<string>>(new Set());
  // Dialogue-level feedback
  const [showDialogueFeedback, setShowDialogueFeedback] = useState(false);
  const [dialogueFeedbackDismissed, setDialogueFeedbackDismissed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Notify parent when any modal opens/closes
  const anyModalOpen = chatModalNode !== null || customPhraseParent !== null || fixResponseNode !== null || practiceNode !== null;
  useEffect(() => {
    onModalChange?.(anyModalOpen);
  }, [anyModalOpen, onModalChange]);

  const { targetLanguage, nativeLanguage } = useLanguage();
  const { showInlineVocalizations, userProfile } = useSettings();
  const { rewrite: rewriteNodes } = useNodeRewrite();
  const t = useTranslations();
  const { speak, stop, state: ttsState } = useTTS({ lang: targetLanguage.code });
  const { prefetch: prefetchVocab, get: getPrefetchedVocab, getPromise: getPrefetchPromise } = useVocabPrefetch();

  // Merge initial nodes with dynamic nodes
  const nodes = useMemo(() => {
    const nodeMap = new Map(initialNodes.filter(n => n != null).map(n => [n.id, n]));
    // Apply dynamic nodes (they may override existing ones with updated children)
    for (const dn of dynamicNodes) {
      nodeMap.set(dn.id, dn);
    }
    return Array.from(nodeMap.values());
  }, [initialNodes, dynamicNodes]);

  // Helper to speak and track which node is speaking
  const speakNode = useCallback((nodeId: string, text: string) => {
    setSpeakingNodeId(nodeId);
    speak(text);
  }, [speak]);

  // Clear speaking node when playback stops
  useEffect(() => {
    if (ttsState === "idle" && speakingNodeId) {
      setSpeakingNodeId(null);
    }
  }, [ttsState, speakingNodeId]);

  // Scroll to node after expansion
  useEffect(() => {
    if (scrollToNodeId && containerRef.current) {
      // Small delay to ensure the DOM has updated
      const timer = setTimeout(() => {
        const nodeElement = containerRef.current?.querySelector(`[data-node-id="${scrollToNodeId}"]`);
        if (nodeElement) {
          nodeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setScrollToNodeId(null);
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [scrollToNodeId]);

  // Idle timer: show feedback after 3 minutes of inactivity (with 2+ selections)
  useEffect(() => {
    if (dialogueFeedbackDismissed || showDialogueFeedback) return;
    if (selectedChild.size < 2) return;

    const timer = setTimeout(() => {
      setShowDialogueFeedback(true);
    }, 3 * 60 * 1000);

    return () => clearTimeout(timer);
  }, [selectedChild, dialogueFeedbackDismissed, showDialogueFeedback]);

  // Build dictionary from vocabulary for word lookups
  const dictionary = useMemo(() => buildDictionary(vocabulary), [vocabulary]);

  // Handle custom phrase creation from modal
  const handleCustomPhraseCreated = useCallback((updatedParent: DialogNode, userNode: DialogNode, responseNode: DialogNode) => {
    setDynamicNodes(prev => {
      const updated = [...prev.filter(n => n.id !== updatedParent.id), updatedParent, userNode, responseNode];
      onNodesChange?.(updated);
      return updated;
    });

    setExpandedNodes(prev => new Set([...prev, updatedParent.id, userNode.id]));
    setSelectedChild(prev => {
      const next = new Map(prev);
      next.set(updatedParent.id, userNode.id);
      return next;
    });
  }, [onNodesChange]);

  // Handle feedback submission
  const handleFeedback = useCallback(async (node: DialogNode, rating: "up" | "down") => {
    // Toggle off if same rating clicked again
    const currentRating = feedbackMap.get(node.id);
    if (currentRating === rating) {
      setFeedbackMap(prev => {
        const next = new Map(prev);
        next.delete(node.id);
        return next;
      });
      return;
    }

    // Update local state immediately
    setFeedbackMap(prev => {
      const next = new Map(prev);
      next.set(node.id, rating);
      return next;
    });

    // Send to API
    try {
      await api.POST("/api/feedback", {
        body: {
          traceId,
          nodeId: node.id,
          nodeText: node.text,
          nodeTranslation: node.translation,
          nodeType: node.type,
          rating: rating as "up" | "down",
          topicTitle,
        },
      });
    } catch (error) {
      console.error("Failed to send feedback:", error);
    }
  }, [feedbackMap, topicTitle, traceId]);

  // Handle fix response from modal + trigger background rewrite
  const handleNodeFixed = useCallback((updatedNode: DialogNode, userInput: string) => {
    // 1. Apply fix immediately
    setDynamicNodes(prev => {
      const updated = [...prev.filter(n => n.id !== updatedNode.id), updatedNode];
      onNodesChange?.(updated);
      return updated;
    });

    // 2. Find the original node to get original text/translation
    const originalNode = nodes.find(n => n.id === updatedNode.id);
    if (!originalNode) return;

    // 3. Fire background rewrite for related nodes
    const dialogueNodesSnapshot = nodes.map(n => ({
      id: n.id,
      type: n.type,
      text: n.id === updatedNode.id ? updatedNode.text : n.text,
      translation: n.id === updatedNode.id ? updatedNode.translation : n.translation,
      children: n.children?.map(c => ({ id: c.id })),
    }));

    rewriteNodes(
      {
        fixedNodeId: updatedNode.id,
        originalText: originalNode.text,
        originalTranslation: originalNode.translation,
        fixedText: updatedNode.text,
        fixedTranslation: updatedNode.translation,
        userIntent: userInput,
        targetLanguage: targetLanguage.code,
        nativeLanguage: nativeLanguage.code,
        dialogueNodes: dialogueNodesSnapshot,
        knownLanguages: userProfile.knownLanguages as string[],
      },
      (rewrittenNode) => {
        // Mark node as no longer rewriting, merge update
        setRewritingNodeIds(prev => {
          const next = new Set(prev);
          next.delete(rewrittenNode.id);
          return next;
        });
        setDynamicNodes(prev => {
          const existing = nodes.find(n => n.id === rewrittenNode.id);
          if (!existing) return prev;
          const merged: DialogNode = {
            ...existing,
            text: rewrittenNode.text,
            translation: rewrittenNode.translation,
            vocalizationTip: rewrittenNode.vocalizationTip || existing.vocalizationTip,
          };
          const updated = [...prev.filter(n => n.id !== rewrittenNode.id), merged];
          onNodesChange?.(updated);
          return updated;
        });
      },
    );
  }, [onNodesChange, nodes, rewriteNodes, targetLanguage.code, nativeLanguage.code, userProfile.knownLanguages]);

  const toggleNode = useCallback((nodeId: string, parentId?: string, firstChildId?: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
        // Also clear the selection for this parent when collapsing
        if (parentId) {
          setSelectedChild((prevSelected) => {
            const newSelected = new Map(prevSelected);
            newSelected.delete(parentId);
            return newSelected;
          });
        }
      } else {
        next.add(nodeId);
        // Mark this as the selected child for its parent
        if (parentId) {
          setSelectedChild((prevSelected) => {
            const newSelected = new Map(prevSelected);
            newSelected.set(parentId, nodeId);
            return newSelected;
          });
        }
        // Schedule scroll to first child after render
        if (firstChildId) {
          setScrollToNodeId(firstChildId);
        }
      }
      return next;
    });
    setActiveNode(nodeId);

    // Greedy prefetch vocab for this node (fire and forget)
    const node = nodes.find((n) => n.id === nodeId);
    if (node) {
      prefetchVocab(node.text, node.alternatives);

      // Trigger dialogue feedback after reaching an end node
      if (node.type === "end" && !dialogueFeedbackDismissed) {
        setTimeout(() => setShowDialogueFeedback(true), 1000);
      }
    }
  }, [nodes, prefetchVocab, dialogueFeedbackDismissed]);

  const getNodeById = useCallback((id: string) => nodes.find((n) => n.id === id), [nodes]);

  const renderNode = useCallback((
    nodeId: string,
    depth: number = 0,
    _parentEdges?: unknown[],
    visited: Set<string> = new Set(),
    parentId?: string,
    prevNodeType?: string,
    isStartGlowing?: boolean
  ): React.ReactNode => {
    // Prevent infinite loops from circular references
    if (visited.has(nodeId)) {
      return null;
    }

    const node = getNodeById(nodeId);
    if (!node) return null;

    // Add to visited set for this branch
    const newVisited = new Set(visited);
    newVisited.add(nodeId);

    const isExpanded = expandedNodes.has(nodeId);
    const isActive = activeNode === nodeId;
    const hasChildren = Boolean(node.children && node.children.length > 0);

    // Determine if we should show avatar (different speaker than previous)
    const isUserMessage = node.type === "option";
    const prevWasUser = prevNodeType === "option";
    const showAvatar = prevNodeType === undefined || isUserMessage !== prevWasUser;

    // Filter children to show only selected branch (or all if none selected)
    const selectedChildId = selectedChild.get(nodeId);
    const visibleChildren = hasChildren
      ? selectedChildId
        ? node.children?.filter((edge) => edge.id === selectedChildId)
        : node.children
      : [];

    // Check if this node is a pending choice (parent has no selection yet)
    // Also glow the start node when dialog hasn't started yet
    const isPendingChoice = parentId ? !selectedChild.has(parentId) : (isStartGlowing && node.type === "start");

    // Get first child ID for scroll-to after expansion
    const firstChildId = node.children?.[0]?.id;

    return (
      <div key={nodeId} className="w-full" data-node-id={nodeId}>
        <TreeNode
          node={node}
          isActive={isActive}
          showAvatar={showAvatar}
          speaker={speaker}
          ttsState={speakingNodeId === nodeId ? ttsState : "idle"}
          feedback={feedbackMap.get(nodeId)}
          isPendingChoice={isPendingChoice}
          showVocalization={showInlineVocalizations}
          onToggle={() => toggleNode(nodeId, parentId, firstChildId)}
          onSpeak={(text) => speakNode(nodeId, text)}
          onStop={stop}
          onAskHelp={() => setChatModalNode(node)}
          onFix={() => setFixResponseNode(node)}
          onFeedback={(rating) => handleFeedback(node, rating)}
          onLongPress={() => setPracticeNode(node)}
          isStartNode={node.type === "start"}
          isRewriting={rewritingNodeIds.has(nodeId)}
          t={t}
        />

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="mt-3 space-y-3">
            {/* "Pick your/their answer" hint - show when transitioning between node types */}
            {!selectedChildId && visibleChildren && visibleChildren.length > 0 && (
              () => {
                const firstChild = visibleChildren[0] ? getNodeById(visibleChildren[0].id) : undefined;
                const isUserOptions = firstChild?.type === "option";
                const parentIsUser = node.type === "option";
                const isSingleOption = visibleChildren.length === 1;
                // Only show hint when transitioning between different speaker types
                if (isUserOptions === parentIsUser) return null;

                // Determine the hint text
                let hintText: string;
                const isMultipleUserOptions = isUserOptions && !isSingleOption;
                if (isUserOptions && isSingleOption) {
                  hintText = t.learning.tapToAnswer;
                } else if (isUserOptions) {
                  hintText = t.learning.pickYourAnswer;
                } else if (isSingleOption) {
                  hintText = t.learning.tapToAnswer;
                } else {
                  hintText = t.learning.pickTheirAnswer;
                }

                return (
                  <div className={cn(
                    "flex gap-2",
                    isUserOptions ? "flex-row-reverse" : "flex-row"
                  )}>
                    <div className="w-8 flex-shrink-0 invisible" />
                    <div className="pb-1">
                      <span className="text-xs text-primary font-medium">
                        {hintText}
                      </span>
                      {isMultipleUserOptions && (
                        <span className="block text-[11px] text-muted-foreground mt-0.5">
                          {t.learning.pickYourAnswerHint}
                        </span>
                      )}
                    </div>
                  </div>
                );
              }
            )()}
            {visibleChildren?.map((edge, idx) => {
              // For first child, prev is current node. For others, prev is the previous sibling.
              const prevEdge = visibleChildren[idx - 1];
              const prevType = idx === 0
                ? node.type
                : prevEdge ? getNodeById(prevEdge.id)?.type : undefined;
              const childNode = getNodeById(edge.id);
              const prevChildNode = idx > 0 && prevEdge ? getNodeById(prevEdge.id) : null;
              // Show "or" between consecutive same-type siblings
              const showOr = idx > 0 && childNode?.type === prevChildNode?.type;
              const isUserOption = childNode?.type === "option";
              return (
                <div key={edge.id} {...(idx === 0 && isUserOption ? { "data-coach-mark": "answer-options" } : {})}>
                  {showOr && (
                    <div className={cn(
                      "flex gap-2",
                      isUserOption ? "flex-row-reverse" : "flex-row"
                    )}>
                      <div className="w-8 flex-shrink-0 invisible" />
                      <div className="pt-0.5 pb-2">
                        <span className="text-xs text-muted-foreground italic">{t.learning.or}</span>
                      </div>
                    </div>
                  )}
                  {renderNode(edge.id, depth + 1, node.children, newVisited, nodeId, prevType)}
                </div>
              );
            })}

            {/* "Say something else" button - show only at frontier (no children expanded yet) */}
            {(node.type === "response" || node.type === "start") &&
             visibleChildren?.some(e => getNodeById(e.id)?.type === "option") &&
             !visibleChildren?.some(e => expandedNodes.has(e.id)) && (
              <div className="flex flex-row-reverse gap-2">
                <div className="w-8 flex-shrink-0 invisible" />
                <button
                  type="button"
                  onClick={() => setCustomPhraseParent(node)}
                  className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-primary border border-dashed border-border hover:border-primary rounded-xl transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t.learning.saySomethingElse || "I want to say something else"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [expandedNodes, activeNode, selectedChild, getNodeById, toggleNode, speakNode, ttsState, speakingNodeId, showInlineVocalizations, rewritingNodeIds, t, setCustomPhraseParent, setFixResponseNode, setPracticeNode, feedbackMap, handleFeedback]);

  // Find the start node (fall back to first node if none explicitly marked)
  const startNode = nodes.find((n) => n.type === "start" || n.id === "start") ?? nodes[0];

  // Check if dialog hasn't started yet (nothing expanded)
  const isDialogNotStarted = expandedNodes.size === 0 && selectedChild.size === 0;

  // Build a node map for O(1) lookups
  const nodeMap = useMemo(() => new Map(nodes.filter(n => n?.id != null).map(n => [n.id, n])), [nodes]);

  // Detect if user has caught up to the streaming frontier
  const isUserWaiting = useMemo(() => {
    if (!isStreaming || !startNode) return false;
    let current = startNode;
    while (true) {
      if (!expandedNodes.has(current.id)) return false; // user hasn't reached here yet
      const selectedId = selectedChild.get(current.id);
      if (!selectedId) {
        // Node is expanded but no child selected — check if children exist
        const hasUnresolvedChildren = current.children?.some(e => !nodeMap.has(e.id));
        return !current.children?.length || !!hasUnresolvedChildren;
      }
      const next = nodeMap.get(selectedId);
      if (!next) return true; // selected child doesn't exist in nodes yet
      current = next;
    }
  }, [isStreaming, startNode, expandedNodes, selectedChild, nodeMap]);

  return (
    <WordLookup dictionary={dictionary} className="px-4 py-4">
      {/* Tap to start hint - clickable */}
      {isDialogNotStarted && startNode && (
        <div className="flex justify-center mb-3">
          <button
            type="button"
            onClick={() => {
              const firstChildId = startNode.children?.[0]?.id;
              toggleNode(startNode.id, undefined, firstChildId);
            }}
            className="text-xs text-primary font-medium hover:text-primary/80 transition-colors cursor-pointer animate-pulse-glow px-3 py-1 rounded-full"
          >
            {t.learning.tapToStart}
          </button>
        </div>
      )}

      {/* Tree */}
      <div ref={containerRef} className="space-y-4 pb-8">
        {startNode && renderNode(startNode.id, 0, undefined, new Set(), undefined, undefined, isDialogNotStarted)}
        {isUserWaiting && (
          <div className="flex justify-center py-6">
            <div className="flex items-center gap-2 text-xs text-muted-foreground animate-pulse">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{t.learning.generatingMore || "Generating..."}</span>
            </div>
          </div>
        )}
      </div>

      {/* Reset button - show after 2+ selections */}
      {selectedChild.size >= 2 && (
        <div className="flex flex-row-reverse gap-2 mt-4">
          <div className="w-8 flex-shrink-0 invisible" />
          <button
            type="button"
            onClick={() => {
              setExpandedNodes(new Set());
              setActiveNode(null);
              setSelectedChild(new Map());
              setShowDialogueFeedback(false);
              setDialogueFeedbackDismissed(false);
            }}
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-primary border border-dashed border-border hover:border-primary rounded-xl transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {t.learning.startAgain}
          </button>
        </div>
      )}

      {/* Dialogue-level feedback card */}
      {showDialogueFeedback && !dialogueFeedbackDismissed && (
        <div className="mt-4 px-2">
          <DialogueFeedbackCard
            topicTitle={topicTitle}
            traceId={traceId}
            nodesExplored={selectedChild.size}
            onDismiss={() => {
              setShowDialogueFeedback(false);
              setDialogueFeedbackDismissed(true);
            }}
          />
        </div>
      )}

      {/* Phrase chat modal */}
      <PhraseChatModal
        isOpen={chatModalNode !== null}
        onClose={() => setChatModalNode(null)}
        phrase={chatModalNode?.text || ""}
        translation={chatModalNode?.translation || ""}
        nodeId={chatModalNode?.id || ""}
      />

      {/* Custom phrase modal */}
      {customPhraseParent && (
        <CustomPhraseModal
          parentNode={customPhraseParent}
          speaker={speaker}
          topicTitle={topicTitle}
          allNodes={nodes}
          onClose={() => setCustomPhraseParent(null)}
          onNodesCreated={handleCustomPhraseCreated}
        />
      )}

      {/* Fix response modal */}
      {fixResponseNode && (
        <FixResponseModal
          node={fixResponseNode}
          speaker={speaker}
          topicTitle={topicTitle}
          onClose={() => setFixResponseNode(null)}
          onNodeFixed={handleNodeFixed}
        />
      )}

      {/* Practice modal (long press) */}
      {practiceNode && (
        <NodePracticeModal
          node={practiceNode}
          vocabulary={vocabulary}
          prefetchedVocab={getPrefetchedVocab(practiceNode.text, practiceNode.alternatives)}
          prefetchPromise={getPrefetchPromise(practiceNode.text, practiceNode.alternatives)}
          onClose={() => setPracticeNode(null)}
        />
      )}
    </WordLookup>
  );
}
